import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import DashboardHeader from '../CompanyComponents/DashboardHeaderRouter';
import SupplierKhata from './SupplierKhata';
import LoadingScreen from '../../Components/LoadingScreen';
import { useBackspaceNavigation } from '../../hooks/useBackspaceNavigation';

function SupplierKhataP2Wrapper() {
  const navigate = useNavigate();

  // Backspace navigation to dashboard
  useBackspaceNavigation();

  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('ur');
  const [ledgerParties, setLedgerParties] = useState([]);
  const [selectedLedgerParty, setSelectedLedgerParty] = useState(null);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [ledgerPartyForm, setLedgerPartyForm] = useState({
    name: '',
    nameUrdu: '',
    type: 'supplier',
    phone: '',
    address: '',
    openingBalance: '',
    balanceType: 'payable'
  });
  const [ledgerTransactionForm, setLedgerTransactionForm] = useState({
    date: new Date().toLocaleDateString('en-CA'),
    description: '',
    type: 'debit',
    amount: ''
  });
  const [supplierSearchFilter, setSupplierSearchFilter] = useState('');
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [editingPartyId, setEditingPartyId] = useState(null);
  const [bills, setBills] = useState([]);

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  // Refresh bills when supplier is selected (to get latest bills)
  useEffect(() => {
    if (selectedLedgerParty?._id && company) {
      fetchBills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLedgerParty?._id, company]);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  const fetchCompanyProfile = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      if (!token) {
        navigate('/company/login');
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      const companyData = response.data.company;
      setCompany(companyData);
      const lang = companyData.websiteLanguage || companyData.language || 'ur';
      setCompanyLanguage(lang);
      localStorage.setItem('companyLanguage', lang);

      // Use allSettled to ensure all API calls complete even if some fail
      await Promise.allSettled([
        fetchLedgerParties(),
        fetchBills()
      ]);
    } catch (error) {
      console.error('Fetch company error:', error);
      toast.error('Failed to load company data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLedgerParties = async () => {
    try {
      setIsLoadingLedger(true);
      const token = getToken();
      const response = await api.get("/ledger/parties?type=supplier", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error('Fetch ledger parties error:', error);
      const lang = getLanguage();
      const errorMessage = error.response?.status === 500
        ? (lang === 'ur' ? 'پارٹیاں لوڈ کرنے میں خرابی' : 'Error loading parties')
        : (lang === 'ur' ? 'پارٹیاں لوڈ نہیں ہو سکیں' : 'Failed to load parties');
      toast.error(errorMessage, { autoClose: 3000 });
      setLedgerParties([]); // Set empty array on error
    } finally {
      setIsLoadingLedger(false);
    }
  };

  const getLanguage = () => {
    return companyLanguage || localStorage.getItem('companyLanguage') || 'ur';
  };

  const fetchBills = async () => {
    try {
      const token = getToken();
      if (!token) {
        return;
      }

      const response = await api.get("/bill/company/my-bills", {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle different response structures
      let billsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          billsData = response.data;
        } else if (response.data.bills && Array.isArray(response.data.bills)) {
          billsData = response.data.bills;
        } else if (response.data.bill && Array.isArray(response.data.bill)) {
          billsData = response.data.bill;
        }
      }

      setBills(billsData);
    } catch (error) {
      console.error('Fetch bills error:', error);
      const lang = getLanguage();
      const errorMessage = error.response?.status === 500
        ? (lang === 'ur' ? 'بلز لوڈ کرنے میں خرابی' : 'Error loading bills')
        : (lang === 'ur' ? 'بلز لوڈ نہیں ہو سکے' : 'Failed to load bills');
      toast.error(errorMessage, { autoClose: 3000 });
      setBills([]);
    }
  };

  const fetchLedgerTransactions = async (partyId) => {
    if (!partyId) return;
    try {
      setIsLoadingLedger(true);
      const token = getToken();
      const response = await api.get(`/ledger/parties/${partyId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLedgerParty(response.data.party);
      setLedgerTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Fetch ledger transactions error:', error);
      const lang = getLanguage();
      const errorMessage = error.response?.status === 500
        ? (lang === 'ur' ? 'لین دین لوڈ کرنے میں خرابی' : 'Error loading transactions')
        : (lang === 'ur' ? 'لین دین لوڈ نہیں ہو سکے' : 'Failed to load transactions');
      toast.error(errorMessage, { autoClose: 3000 });
      setLedgerTransactions([]); // Set empty array on error
    } finally {
      setIsLoadingLedger(false);
    }
  };

  const handleLedgerPartyFormChange = (e) => {
    const { name, value } = e.target;
    setLedgerPartyForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateLedgerParty = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const cleanedOpening = String(ledgerPartyForm.openingBalance || '').replace(/[^0-9.-]/g, '');
      const openingValue = cleanedOpening ? parseFloat(cleanedOpening) : 0;

      const partyData = {
        ...ledgerPartyForm,
        type: 'supplier',
        openingBalance: isNaN(openingValue) ? 0 : openingValue
      };

      if (editingPartyId) {
        await api.put(`/ledger/parties/${editingPartyId}`, partyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(companyLanguage === 'ur' ? 'پارٹی اپ ڈیٹ ہو گئی' : 'Party updated');
      } else {
        await api.post('/ledger/parties', partyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(companyLanguage === 'ur' ? 'پارٹی بنا دی گئی' : 'Party created');
      }

      setLedgerPartyForm({
        name: '',
        nameUrdu: '',
        type: 'supplier',
        phone: '',
        address: '',
        openingBalance: '',
        balanceType: 'payable'
      });
      setEditingPartyId(null);
      setShowSupplierForm(false);
      fetchLedgerParties();
    } catch (error) {
      console.error('Create/Update ledger party error:', error);
      toast.error(companyLanguage === 'ur' ? 'پارٹی محفوظ نہیں ہو سکی' : 'Failed to save party');
    }
  };

  const handleEditLedgerParty = (party) => {
    setEditingPartyId(party._id);
    setLedgerPartyForm({
      name: party.name || '',
      nameUrdu: party.nameUrdu || '',
      type: party.type || 'supplier',
      phone: party.phone || '',
      address: party.address || '',
      openingBalance: party.openingBalance || '',
      balanceType: party.balanceType || 'payable'
    });
    setShowSupplierForm(true);
  };

  const handleDeleteLedgerParty = async (partyId) => {
    const confirmMsg = companyLanguage === 'ur'
      ? 'کیا آپ واقعی اس پارٹی کو حذف کرنا چاہتے ہیں؟'
      : 'Are you sure you want to delete this party?';

    if (!window.confirm(confirmMsg)) return;

    try {
      const token = getToken();
      await api.delete(`/ledger/parties/${partyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'پارٹی حذف ہو گئی' : 'Party deleted');
      fetchLedgerParties();
      if (selectedLedgerParty && selectedLedgerParty._id === partyId) {
        setSelectedLedgerParty(null);
        setLedgerTransactions([]);
      }
    } catch (error) {
      console.error('Delete ledger party error:', error);
      toast.error(companyLanguage === 'ur' ? 'پارٹی حذف نہیں ہو سکی' : 'Failed to delete party');
    }
  };

  const handleLedgerTransactionFormChange = (e) => {
    const { name, value } = e.target;
    setLedgerTransactionForm(prev => ({
      ...prev,
      [name]: name === 'amount' ? value.replace(/[^0-9.]/g, '') : value
    }));
  };

  const handleCreateLedgerTransaction = async (e) => {
    e.preventDefault();
    if (!selectedLedgerParty) {
      toast.error(companyLanguage === 'ur' ? 'پہلے پارٹی منتخب کریں' : 'Select a party first');
      return;
    }
    const amountNum = Number(ledgerTransactionForm.amount || 0);
    if (!amountNum) {
      toast.error(companyLanguage === 'ur' ? 'رقم درج کریں' : 'Enter amount');
      return;
    }
    try {
      const token = getToken();
      await api.post(`/ledger/parties/${selectedLedgerParty._id}/transactions`, {
        date: ledgerTransactionForm.date,
        description: ledgerTransactionForm.description,
        type: ledgerTransactionForm.type,
        amount: amountNum
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'اندراج شامل ہو گیا' : 'Entry added');
      setLedgerTransactionForm({
        date: new Date().toLocaleDateString('en-CA'),
        description: '',
        type: 'debit',
        amount: ''
      });
      fetchLedgerTransactions(selectedLedgerParty._id);
    } catch (error) {
      console.error('Create ledger transaction error:', error);
      toast.error(companyLanguage === 'ur' ? 'اندراج محفوظ نہیں ہو سکا' : 'Failed to save entry');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    navigate("/company/login");
    toast.info(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
  };

  // Get page permissions from company
  const getPagePermission = (pageKey) => {
    if (!company?.pagePermissions) return true; // Default to enabled if no permissions set
    // Convert Map to object if needed, or use directly if already object
    const permissions = company.pagePermissions instanceof Map
      ? Object.fromEntries(company.pagePermissions)
      : company.pagePermissions;
    return permissions[pageKey] !== false; // Default to true if not explicitly false
  };

  // Check permission for supplier khata - both keys must be checked
  const hasPermission = getPagePermission('supplier-khata-p2') && getPagePermission('supplierKhata');

  if (isLoading) {
    return (
      <LoadingScreen
        companyLanguage={companyLanguage}
        message={companyLanguage === "ur" ? "لوڈ ہو رہا ہے..." : "Loading..."}
      />
    );
  }

  // Redirect if no permission
  if (!hasPermission) {
    navigate('/company/dashboard-part2');
    toast.error(companyLanguage === 'ur' ? 'آپ کو اس صفحے تک رسائی نہیں ہے' : 'You do not have access to this page');
    return null;
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
      <DashboardHeader
        company={company}
        companyLanguage={companyLanguage}
        onLogout={handleLogout}
        bills={[]}
        sales={[]}
        recoveries={[]}
        persons={[]}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 pt-6 pb-8">
        <SupplierKhata
          company={company}
          companyLanguage={companyLanguage}
          ledgerParties={ledgerParties}
          supplierSearchFilter={supplierSearchFilter}
          setSupplierSearchFilter={setSupplierSearchFilter}
          showSupplierForm={showSupplierForm}
          setShowSupplierForm={setShowSupplierForm}
          ledgerPartyForm={ledgerPartyForm}
          handleLedgerPartyFormChange={handleLedgerPartyFormChange}
          handleCreateLedgerParty={handleCreateLedgerParty}
          editingPartyId={editingPartyId}
          setEditingPartyId={setEditingPartyId}
          isLoadingLedger={isLoadingLedger}
          selectedLedgerParty={selectedLedgerParty}
          fetchLedgerTransactions={fetchLedgerTransactions}
          handleEditLedgerParty={handleEditLedgerParty}
          handleDeleteLedgerParty={handleDeleteLedgerParty}
          ledgerTransactions={ledgerTransactions}
          ledgerTransactionForm={ledgerTransactionForm}
          handleLedgerTransactionFormChange={handleLedgerTransactionFormChange}
          handleCreateLedgerTransaction={handleCreateLedgerTransaction}
          activeTab="ledgers"
          activeLedgerTab="supplierKhata"
          setLedgerPartyForm={setLedgerPartyForm}
          bills={bills}
        />
      </div>
    </div>
  );
}

export default SupplierKhataP2Wrapper;

